import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTable,
  faUsers,
  faChair,
  faCheckCircle,
  faTools,
  faClock,
  faSpinner,
  faTimes,
  faRefresh,
  faSearch,
  faFilter,
  faPlus,
  faEdit,
  faTrash,
  faClose,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";

// API fetcher for JSON:API
const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    },
  });
  if (!response.ok)
    throw new Error(`API request failed with status ${response.status}`);
  return response.json();
};

// Table status configuration
const TABLE_STATUS = {
  available: {
    label: "Available",
    color: "#10B981",
    bgColor: "#D1FAE5",
    icon: faCheckCircle,
  },
  occupied: {
    label: "Occupied",
    color: "#EF4444",
    bgColor: "#FEE2E2",
    icon: faUsers,
  },
  reserved: {
    label: "Reserved",
    color: "#F59E0B",
    bgColor: "#FEF3C7",
    icon: faClock,
  },
  maintenance: {
    label: "Maintenance",
    color: "#6B7280",
    bgColor: "#F3F4F6",
    icon: faTools,
  },
};

export default function TableManager() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddTableModal, setShowAddTableModal] = useState(false);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    table_number: "",
    capacity: "",
    status: "available",
    table_display: "",
  });
  const [editFormData, setEditFormData] = useState({
    table_number: "",
    capacity: "",
    status: "available",
    table_display: "",
  });

  // Fetch CSRF token from Drupal
  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );
    if (!response.ok) throw new Error("Failed to fetch CSRF token");
    return await response.text();
  };

  // Fetch tables from Drupal JSON:API
  const {
    data: tablesData,
    error: tablesError,
    isLoading: tablesLoading,
    mutate: mutateTables,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/restaurant_tables?sort=drupal_internal__tid`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000,
    },
  );

  // Process tables data from JSON:API response
  const tables = useMemo(() => {
    if (!tablesData || !tablesData.data) return [];

    return tablesData.data.map((item) => {
      const attributes = item.attributes;
      const tableNumberMatch = attributes.name.match(/(\d+)/);
      const tableNumber = tableNumberMatch ? parseInt(tableNumberMatch[0]) : 0;

      return {
        id: item.id,
        tid: attributes.drupal_internal__tid,
        table_number: tableNumber,
        table_display: attributes.name,
        capacity: attributes.field_table_capacity || 2,
        status: attributes.field_table_status || "available",
      };
    });
  }, [tablesData]);

  // Filter tables
  const filteredTables = useMemo(() => {
    let filtered = [...tables];

    if (statusFilter !== "all") {
      filtered = filtered.filter((table) => table.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (table) =>
          table.table_number.toString().includes(searchTerm) ||
          (table.table_display &&
            table.table_display
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      );
    }

    return filtered;
  }, [tables, statusFilter, searchTerm]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalTables = tables.length;
    const availableTables = tables.filter(
      (t) => t.status === "available",
    ).length;
    const occupiedTables = tables.filter((t) => t.status === "occupied").length;
    const reservedTables = tables.filter((t) => t.status === "reserved").length;
    const maintenanceTables = tables.filter(
      (t) => t.status === "maintenance",
    ).length;
    const totalCapacity = tables.reduce((sum, t) => sum + (t.capacity || 0), 0);
    const occupancyRate =
      totalTables > 0 ? ((occupiedTables / totalTables) * 100).toFixed(1) : 0;

    return {
      totalTables,
      availableTables,
      occupiedTables,
      reservedTables,
      maintenanceTables,
      totalCapacity,
      occupancyRate,
    };
  }, [tables]);

  const handleRefresh = () => {
    mutateTables();
  };

  // Add new table
  const handleAddTable = async (e) => {
    e.preventDefault();

    if (!formData.table_number) {
      setErrorMessage("Table number is required");
      setModalError(true);
      return;
    }

    if (!formData.capacity || formData.capacity < 1) {
      setErrorMessage("Valid capacity is required");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setIsSubmitting(false);
      return;
    }

    const tableDisplay =
      formData.table_display || `Table ${formData.table_number}`;

    const payload = {
      data: {
        type: "taxonomy_term--restaurant_tables",
        attributes: {
          name: tableDisplay,
          field_table_capacity: parseInt(formData.capacity),
          field_table_status: formData.status,
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/restaurant_tables`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        await mutateTables();
        setFormData({
          table_number: "",
          capacity: "",
          status: "available",
          table_display: "",
        });
        setShowAddTableModal(false);
        setSuccessMessage(`Table ${tableDisplay} added successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(
          errorData.errors?.[0]?.detail || "Failed to create table",
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating table:", err);
      setErrorMessage("An error occurred while creating the table");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Edit table
  const handleEditTable = async (e) => {
    e.preventDefault();

    if (!editFormData.table_number) {
      setErrorMessage("Table number is required");
      setModalError(true);
      return;
    }

    if (!editFormData.capacity || editFormData.capacity < 1) {
      setErrorMessage("Valid capacity is required");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setIsSubmitting(false);
      return;
    }

    const tableDisplay =
      editFormData.table_display || `Table ${editFormData.table_number}`;

    const payload = {
      data: {
        type: "taxonomy_term--restaurant_tables",
        id: selectedTable.id,
        attributes: {
          name: tableDisplay,
          field_table_capacity: parseInt(editFormData.capacity),
          field_table_status: editFormData.status,
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/restaurant_tables/${selectedTable.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        await mutateTables();
        setShowEditTableModal(false);
        setSelectedTable(null);
        setEditFormData({
          table_number: "",
          capacity: "",
          status: "available",
          table_display: "",
        });
        setSuccessMessage(`Table ${tableDisplay} updated successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(
          errorData.errors?.[0]?.detail || "Failed to update table",
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error updating table:", err);
      setErrorMessage("An error occurred while updating the table");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Delete table
  const handleDeleteTable = async (table) => {
    if (
      !window.confirm(`Are you sure you want to delete ${table.table_display}?`)
    ) {
      return;
    }

    setIsSubmitting(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/restaurant_tables/${table.id}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
        },
      );

      if (response.ok) {
        await mutateTables();
        setSuccessMessage(`${table.table_display} deleted successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        setErrorMessage(
          errorData.errors?.[0]?.detail || "Failed to delete table",
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error deleting table:", err);
      setErrorMessage("An error occurred while deleting the table");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  const openEditModal = (table) => {
    setSelectedTable(table);
    setEditFormData({
      table_number: table.table_number,
      capacity: table.capacity,
      status: table.status,
      table_display: table.table_display,
    });
    setShowEditTableModal(true);
  };

  const TableCard = ({ table }) => {
    const status = TABLE_STATUS[table.status] || TABLE_STATUS.available;
    const StatusIcon = status.icon;

    return (
      <div
        style={{
          ...styles.tableCard,
          borderTopColor: status.color,
          opacity: table.status === "maintenance" ? 0.7 : 1,
        }}
      >
        <div style={styles.tableCardHeader}>
          <div
            style={{
              ...styles.tableStatusBadge,
              backgroundColor: status.bgColor,
              color: status.color,
            }}
          >
            <FontAwesomeIcon
              icon={StatusIcon}
              style={{ fontSize: "10px", marginRight: "4px" }}
            />
            {status.label}
          </div>
          <div style={styles.tableActions}>
            <button
              style={styles.editTableButton}
              onClick={() => openEditModal(table)}
              title="Edit Table"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              style={styles.deleteTableButton}
              onClick={() => handleDeleteTable(table)}
              title="Delete Table"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>

        <div style={styles.tableNumberLarge}>{table.table_number}</div>
        <div style={styles.tableDisplayName}>{table.table_display}</div>

        <div style={styles.tableDetails}>
          <div style={styles.tableDetailItem}>
            <FontAwesomeIcon
              icon={faChair}
              style={{ fontSize: "12px", color: "#6B7280" }}
            />
            <span style={styles.tableDetailText}>
              Capacity: {table.capacity} guests
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (tablesLoading && !tablesData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading tables...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (tablesError) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Tables</h3>
              <p>
                Unable to connect to the tables API. Please check your
                connection.
              </p>
              <button style={styles.retryButton} onClick={handleRefresh}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus AddTitle="Table Manager" />

          {/* STATISTICS CARDS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faTable}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalTables}</h3>
                <p style={styles.statLabel}>Total Tables</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#D1FAE5",
                }}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  style={{ color: "#10B981", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.availableTables}</h3>
                <p style={styles.statLabel}>Available</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEE2E2",
                }}
              >
                <FontAwesomeIcon
                  icon={faUsers}
                  style={{ color: "#EF4444", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.occupiedTables}</h3>
                <p style={styles.statLabel}>Occupied</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEF3C7",
                }}
              >
                <FontAwesomeIcon
                  icon={faClock}
                  style={{ color: "#F59E0B", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.reservedTables}</h3>
                <p style={styles.statLabel}>Reserved</p>
              </div>
            </div>
          </div>

          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3F4F6",
                }}
              >
                <FontAwesomeIcon
                  icon={faTools}
                  style={{ color: "#6B7280", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.maintenanceTables}</h3>
                <p style={styles.statLabel}>Maintenance</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faChair}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalCapacity}</h3>
                <p style={styles.statLabel}>Total Capacity</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FCE4EC",
                }}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{ color: "#E91E63", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.occupancyRate}%</h3>
                <p style={styles.statLabel}>Occupancy Rate</p>
              </div>
            </div>

            {/* Add Table Button Card */}
            <div style={styles.statCard}>
              <button
                style={styles.addTableButton}
                onClick={() => setShowAddTableModal(true)}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  style={{ fontSize: "20px", marginRight: "8px" }}
                />
                Add New Table
              </button>
            </div>
          </div>

          {/* FILTERS BAR */}
          <div style={styles.filtersBar}>
            <div style={styles.searchWrapper}>
              <FontAwesomeIcon icon={faSearch} style={{ color: "#9CA3AF" }} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search by table number or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div style={styles.filterWrapper}>
              <FontAwesomeIcon icon={faFilter} style={{ color: "#9CA3AF" }} />
              <select
                style={styles.statusSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Tables</option>
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="reserved">Reserved</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>

            <button style={styles.refreshFilterButton} onClick={handleRefresh}>
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>

          {/* TABLES GRID */}
          {filteredTables.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faTable}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Tables Found</h3>
              <p style={styles.emptySubtitle}>
                {searchTerm || statusFilter !== "all"
                  ? "No tables match your current filters"
                  : "Click 'Add New Table' to create your first table"}
              </p>
            </div>
          ) : (
            <div style={styles.tablesGrid}>
              {filteredTables.map((table) => (
                <TableCard key={table.id} table={table} />
              ))}
            </div>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {/* Add Table Modal */}
      {showAddTableModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Add New Table</p>
                <button
                  className="close-modal"
                  onClick={() => setShowAddTableModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleAddTable} className="edit-patient-form">
                <div className="form-group">
                  <label>Table Number *</label>
                  <input
                    type="number"
                    placeholder="Enter Table Number"
                    value={formData.table_number}
                    onChange={(e) =>
                      setFormData({ ...formData, table_number: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Table Display Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Table 1, VIP Section A"
                    value={formData.table_display}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        table_display: e.target.value,
                      })
                    }
                  />
                  <small
                    style={{
                      color: "#6B7280",
                      fontSize: "11px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    Leave blank to use "Table {"{"}number{"}"}"
                  </small>
                </div>

                <div className="form-group">
                  <label>Capacity *</label>
                  <input
                    type="number"
                    placeholder="Enter Table Capacity"
                    value={formData.capacity}
                    onChange={(e) =>
                      setFormData({ ...formData, capacity: e.target.value })
                    }
                    required
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={styles.submitButton}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Add Table"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {showEditTableModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>Edit Table</p>
                <button
                  className="close-modal"
                  onClick={() => setShowEditTableModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleEditTable} className="edit-patient-form">
                <div className="form-group">
                  <label>Table Number *</label>
                  <input
                    type="number"
                    placeholder="Enter Table Number"
                    value={editFormData.table_number}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        table_number: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Table Display Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Table 1, VIP Section A"
                    value={editFormData.table_display}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        table_display: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Capacity *</label>
                  <input
                    type="number"
                    placeholder="Enter Table Capacity"
                    value={editFormData.capacity}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        capacity: e.target.value,
                      })
                    }
                    required
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div className="submitGroup">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={styles.submitButton}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Update Table"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faTimes}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  statIconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#1F2937",
  },
  statLabel: {
    margin: "4px 0 0 0",
    color: "#6B7280",
    fontSize: "14px",
    fontWeight: "500",
  },
  addTableButton: {
    background: "#FF6B35",
    color: "white",
    border: "none",
    padding: "12px 20px",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  filtersBar: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchWrapper: {
    flex: 2,
    minWidth: "250px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  searchInput: {
    flex: 1,
    border: "none",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "transparent",
  },
  filterWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  statusSelect: {
    border: "none",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    padding: "4px 8px",
  },
  refreshFilterButton: {
    padding: "10px 16px",
    backgroundColor: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    cursor: "pointer",
    color: "#6B7280",
  },
  tablesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
  },
  tableCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    borderTopWidth: "4px",
    borderTopStyle: "solid",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  tableCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
  },
  tableStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
  },
  tableActions: {
    display: "flex",
    gap: "8px",
  },
  editTableButton: {
    background: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  deleteTableButton: {
    background: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  tableNumberLarge: {
    fontSize: "36px",
    fontWeight: "700",
    color: "#1F2937",
    lineHeight: 1,
    marginBottom: "4px",
  },
  tableDisplayName: {
    fontSize: "14px",
    color: "#6B7280",
    marginBottom: "16px",
  },
  tableDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  tableDetailItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  tableDetailText: {
    fontSize: "13px",
    color: "#6B7280",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "14px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
  submitButton: {
    background: "#333",
    color: "white",
    padding: "10px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
};
